import { pipe } from 'ramda'
import moment from 'moment'
import {
  OrderDetails,
  OrderEdit,
  OrderListItem
} from 'src/features/orders/duck/records'
import {
  filterEmptyStrings,
  filterNullValues,
  getDateFromIsoString,
  getIsoStringFromDateAndTime,
  getTimeFromIsoString
} from 'src/utils/helpers'
import {
  AdrClass,
  CargoType,
  Company,
  Container,
  Contractor,
  Currency,
  Shipowner
} from 'src/ducks/records'
import { TRACKING_OFFER_TYPES } from 'src/features/tracking/duck/consts'
import {
  normalizeExportOfferDetails,
  normalizeGeneralOfferDetails,
  normalizeFreeCarrierOfferDetails,
  normalizeImportOfferDetails,
  normalizePostImportOfferDetails
} from 'src/features/stocks/ducks/normalizers'
import { DATE_SHORT_FORMAT_WITH_TIME } from 'src/ducks/consts'

const normalizeDateTimeLabel = value =>
  value ? moment(value).format(DATE_SHORT_FORMAT_WITH_TIME) : null

const normalizeDateTimeFields = (dateTime, prefix) => ({
  [`${prefix}Date`]: getDateFromIsoString(dateTime),
  [`${prefix}Time`]: getTimeFromIsoString(dateTime)
})

const getGeneralOrderSavePayload = obj => ({
  ...obj,
  shipownerId: null,
  pickUpAddress: null,
  pickUpRef: null,
  pin: null,
  returnAddress: null,
  booking: null,
  returnRef: null,
  loadingUnloadingAddress: null,
  loadingUnloadingCompanyName: null,
  loadingUnloadingDateTime: null,
  loadingUnloadingDate: null,
  loadingUnloadingTime: null,
  loadingDateTime: getIsoStringFromDateAndTime(
    obj.loadingDate,
    obj.loadingTime
  ),
  unloadingDateTime: getIsoStringFromDateAndTime(
    obj.unloadingDate,
    obj.unloadingTime
  )
})

export const normalizeOrdersList = arr =>
  arr.map(
    el =>
      new OrderListItem({
        ...el,
        loadingUnloadingDateTime: normalizeDateTimeLabel(
          el.loadingUnloadingDateTime
        ),
        loadingDateTime: normalizeDateTimeLabel(el.loadingDateTime),
        unloadingDateTime: normalizeDateTimeLabel(el.unloadingDateTime),
        container: Container({ ...el.container }),
        cargoType: CargoType({ ...el.cargoType }),
        shipowner: Shipowner({ ...el.shipowner }),
        carrierCompany: Company({ ...el.carrierCompany }),
        forwarderCompany: Company({ ...el.forwarderCompany })
      })
  )

export const normalizeOrderDetails = obj => {
  const offer = {
    [TRACKING_OFFER_TYPES.EXPORT]: normalizeExportOfferDetails(obj.offer),
    [TRACKING_OFFER_TYPES.IMPORT]: normalizeImportOfferDetails(obj.offer),
    [TRACKING_OFFER_TYPES.GENERAL]: normalizeGeneralOfferDetails(obj.offer),
    [TRACKING_OFFER_TYPES.FREE_CARRIER]: normalizeFreeCarrierOfferDetails(
      obj.offer
    ),
    [TRACKING_OFFER_TYPES.POST_IMPORT]: normalizePostImportOfferDetails(
      obj.offer
    )
  }[obj.offerType]

  return OrderDetails({
    ...obj,
    ...normalizeDateTimeFields(
      obj.loadingUnloadingDateTime,
      'loadingUnloading'
    ),
    ...normalizeDateTimeFields(obj.loadingDateTime, 'loading'),
    ...normalizeDateTimeFields(obj.unloadingDateTime, 'unloading'),
    container: Container({
      ...obj.container
    }),
    shipowner: Shipowner({
      ...obj.shipowner
    }),
    forwarderUser: Contractor({
      ...obj.forwarderUser
    }),
    carrierUser: Contractor({
      ...obj.carrierUser
    }),
    price: Currency({ ...obj.price }),
    adrClass: AdrClass({ ...obj.adrClass }),
    offer
  })
}

export const normalizeOrderEditForm = obj =>
  OrderEdit({
    ...obj,
    price: obj.price?.float,
    ...normalizeDateTimeFields(
      obj.loadingUnloadingDateTime,
      'loadingUnloading'
    ),
    ...normalizeDateTimeFields(obj.loadingDateTime, 'loading'),
    ...normalizeDateTimeFields(obj.unloadingDateTime, 'unloading')
  })

export const normalizeOrderEditFormForSave = obj =>
  pipe(
    filterNullValues,
    filterEmptyStrings
  )(
    obj.offerType === TRACKING_OFFER_TYPES.GENERAL
      ? getGeneralOrderSavePayload(obj)
      : {
          ...obj,
          loadingDateTime: null,
          unloadingDateTime: null,
          loadingDate: null,
          loadingTime: null,
          loadingCompanyName: null,
          loadingAddress: null,
          unloadingDate: null,
          unloadingTime: null,
          unloadingCompanyName: null,
          unloadingAddress: null,
          loadingUnloadingDateTime: getIsoStringFromDateAndTime(
            obj.loadingUnloadingDate,
            obj.loadingUnloadingTime
          )
        }
  )
